import React, { useState } from 'react';
import {
    Button,
    Box,
    Typography,
    Stack,
    FormControl,
    TextField,
    IconButton,
    DialogContent,
    DialogActions,
    Alert,
} from '@mui/material';
import DialogHeader from '@shared/ui/dialog/DialogHeader';
import { Close } from '@mui/icons-material';
import { APP_CONFIG } from '../../../../config/app.config';
import { container } from '@infrastructure/container';
import { Connection } from '@domain/connection/models/Connection';

interface WooCommerceCredentialsFormProps {
    onNewConnection: (connection: Connection) => void;
    onClose: () => void;
    /** When set, re-authorizes this existing connection instead of creating a new one. */
    reconnectConnectionId?: string;
}

const WooCommerceCredentialsForm: React.FC<WooCommerceCredentialsFormProps> = ({ onNewConnection, onClose, reconnectConnectionId }) => {
    const [url, setUrl] = useState('');
    const [consumerKey, setConsumerKey] = useState('');
    const [consumerSecret, setConsumerSecret] = useState('');

    const [validationError, setValidationError] = useState<{ url: string | null, consumerKey: string | null, consumerSecret: string | null } | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    const connectWithWooCommerce = async () => {
        setError(null);
        setValidationError(null);

        if (!url || !consumerKey || !consumerSecret) {
            setValidationError({ url: 'Please fill in all fields', consumerKey: 'Please fill in all fields', consumerSecret: 'Please fill in all fields' });
            return;
        }

        // Validate URL format
        try {
            new URL(url);
        } catch {
            setValidationError({ url: 'Please enter a valid URL', consumerKey: null, consumerSecret: null });
            return;
        }

        setIsLoading(true);
        try {
            const result = reconnectConnectionId
                ? await container.wooCommerceConnectorRepository.reconnect(reconnectConnectionId, url, consumerKey, consumerSecret)
                : await container.wooCommerceConnectorRepository.addWooCommerceConnection(url, consumerKey, consumerSecret);

            if (result.status === 'error') {
                setError(result.error?.message || 'Failed to connect to WooCommerce');
                setIsLoading(false);
                return;
            }

            if (result.status === 'success' && result.data) {
                // Reconnect already re-runs a full sync on the backend; only kick a product sync for a brand-new connection.
                if (!reconnectConnectionId) {
                    container.wooCommerceConnectorRepository.syncProducts(result.data.id).catch(() => { });
                }
                onNewConnection(result.data);
                onClose();
            } else {
                setError('Failed to connect to WooCommerce. Invalid response.');
            }
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to connect to WooCommerce. Please try again.';
            setError(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            <DialogHeader>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6" fontWeight="medium">
                        {reconnectConnectionId ? 'Reconnect WooCommerce' : 'Connect WooCommerce'}
                    </Typography>

                    <IconButton onClick={onClose} color="inherit">
                        <Close />
                    </IconButton>
                </Stack>
            </DialogHeader>

            <DialogContent>
                <Box>
                    <Typography variant="subtitle2" color="text.primary">
                        To connect to your WooCommerce store, follow these steps:
                    </Typography>

                    <Typography variant="body2" color="text.secondary">
                        <ol style={{ paddingLeft: 20 }}>
                            <li>
                                <Typography variant="body2" color="text.secondary">
                                    Log in to your woocommerce store admin panel
                                </Typography>
                            </li>
                            <li>
                                <Typography variant="body2" color="text.secondary">
                                    Navigate to WooCommerce → Settings → Advanced → REST API
                                </Typography>
                            </li>
                            <li>
                                <Typography variant="body2" color="text.secondary">
                                    Click "Add key" to create a new API key
                                </Typography>
                            </li>
                            <li>
                                <Typography variant="body2" color="text.secondary">
                                    Set the description and permissions
                                </Typography>
                            </li>
                            <li>
                                <Typography variant="body2" color="text.secondary">
                                    Copy the Consumer Key and Consumer Secret, and fill them in the form below
                                </Typography>
                            </li>
                        </ol>
                    </Typography>
                </Box>

                <FormControl fullWidth>
                    <TextField
                        label="Store URL"
                        variant="outlined"
                        fullWidth
                        margin="normal"
                        placeholder="https://yourstore.com"
                        value={url}
                        onChange={(e) => setUrl(e.target.value)}
                        error={!!validationError?.url}
                        helperText={validationError?.url}
                    />
                    <TextField
                        label="Consumer Key"
                        variant="outlined"
                        fullWidth
                        margin="normal"
                        value={consumerKey}
                        onChange={(e) => setConsumerKey(e.target.value)}
                        error={!!validationError?.consumerKey}
                        helperText={validationError?.consumerKey}
                    />
                    <TextField
                        label="Consumer Secret"
                        type="password"
                        variant="outlined"
                        fullWidth
                        margin="normal"
                        value={consumerSecret}
                        onChange={(e) => setConsumerSecret(e.target.value)}
                        error={!!validationError?.consumerSecret}
                        helperText={validationError?.consumerKey}
                    />

                    {error && (
                        <Alert severity="error" sx={{ mt: 2 }}>
                            {error}
                        </Alert>
                    )}
                </FormControl>
            </DialogContent>

            <DialogActions sx={{ borderTop: '1px solid', borderColor: APP_CONFIG.ui.theme.colors.border, p: 3, justifyContent: 'flex-end' }}>
                <Stack direction="row" alignItems="center" gap={1} flex={1} justifyContent="space-between">
                    <Button variant="outlined" onClick={onClose}>Back</Button>
                    <Button
                        variant="contained"
                        disabled={isLoading || !url || !consumerKey || !consumerSecret}
                        onClick={connectWithWooCommerce}
                    >
                        {reconnectConnectionId ? 'Reconnect' : 'Connect'}
                    </Button>
                </Stack>
            </DialogActions>
        </>
    );
};

export default WooCommerceCredentialsForm;
